import { useState, useEffect, useRef } from "react";

import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

import useIsTabletSize from "@hooks/useIsTabletSize.js";
import ErrorMessage from "@common-components/ErrorMessage/ErrorMessage";
import PDFViewer from "@components/PDFViewer/PDFViewer";
import TabletPDFViewer from "@components/PDFViewer/TabletPDFViewer";
import PDFErrorPop from "@components/common/PDFErrorPop/PDFErrorPop";
import Spinner from "@common-components/Spinner/Spinner";

const OptionPageRightPanel = ({
  error,
  isTablet,
  setIsLeftPanelOpen,
  isLeftPanelOpen,
  pdfViewerArray,
  hasUpdatedTopGroupOptions,
  sheetNames,
  isViewerArrayLoading,
}) => {
  const isTabletSize = useIsTabletSize();
  const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
  const prevModelIdRef = useRef(null);
  const prevPdfViewerArrayRef = useRef([]);

  // Determine correct PDF index when pdfViewerArray or options change
  useEffect(() => {
    const modelSelection = JSON.parse(localStorage.getItem("modelSelection"));
    const isSameModel = modelSelection?.id === prevModelIdRef.current;
    let newIndex = currentPdfIndex;

    if (!isSameModel) {
      // New model → reset page-index to 0
      newIndex = 0;
      prevModelIdRef.current = modelSelection?.id;
    } else if (hasUpdatedTopGroupOptions) {
      // Orientation / style change → preserve current index
      newIndex = Math.max(
        0,
        Math.min(currentPdfIndex, pdfViewerArray.length - 1),
      );
    } else {
      // Other options → jump to first different PDF
      let firstDiff = -1;
      for (
        let i = 0;
        i <
        Math.max(prevPdfViewerArrayRef.current.length, pdfViewerArray.length);
        i++
      ) {
        if (prevPdfViewerArrayRef.current[i] !== pdfViewerArray[i]) {
          firstDiff = i;
          break;
        }
      }
      newIndex = firstDiff !== -1 ? firstDiff : currentPdfIndex;
    }

    setCurrentPdfIndex(newIndex);
    prevPdfViewerArrayRef.current = pdfViewerArray;
  }, [pdfViewerArray, currentPdfIndex, hasUpdatedTopGroupOptions]);

  return (
    <div className="OptionPage__container--right">
      {error.pdfPathBundles || error.sheetLib ? (
        <ErrorMessage>{error.pdfPathBundles || error.sheetLib}</ErrorMessage>
      ) : (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {isTablet && isTabletSize && (
            <div
              onClick={() => setIsLeftPanelOpen((prev) => !prev)}
              className="OptionPage__toggleBtn"
            >
              {isLeftPanelOpen ? (
                <CloseIcon className="OptionPage__panelIcon" />
              ) : (
                <MenuIcon className="OptionPage__panelIcon" />
              )}
            </div>
          )}

          {isViewerArrayLoading ? (
            <Spinner />
          ) : pdfViewerArray.length > 0 ? (
            isTablet ? (
              <TabletPDFViewer
                pdfPaths={pdfViewerArray}
                hasUpdatedTopGroupOptions={hasUpdatedTopGroupOptions}
                sheetNames={sheetNames}
                currentPdfIndex={currentPdfIndex}
                setCurrentPdfIndex={setCurrentPdfIndex}
                isTablet={isTablet}
              />
            ) : (
              <PDFViewer
                pdfPaths={pdfViewerArray}
                hasUpdatedTopGroupOptions={hasUpdatedTopGroupOptions}
                sheetNames={sheetNames}
                currentPdfIndex={currentPdfIndex}
                setCurrentPdfIndex={setCurrentPdfIndex}
                isTablet={isTablet}
              />
            )
          ) : (
            <PDFErrorPop
              errorMessage={
                "Oops! The drawing isn\u2019t available at the moment"
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

export default OptionPageRightPanel;
